import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import { FlowForm, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
// import { OpacityContainer } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { authenticatedRequest, getAdminMagicSecret } from '../../connector/auth';
import { RestAdminFeed, RestCatalogAdmin, RestCatalog, RestCatalogCreate, RestCatalogUpdate } from '../../schema-copy/api-schema';
import { RssTypes, FeedCategoryList } from '../../schema-copy/feed-schema';
import { nonNull } from 't-lib-ui/helpers';
import { formatCompactDate } from '../../utils/formatting';
import { TextButton, DropDownSelector, DropDownSelectorValue, Input, Spinner, A, TextArea } from 't-lib-ui/widgets';
import { chevronBackIcon, checkmarkIcon, closeIcon, thumbsUpIcon, thumbsDownIcon, closeCircleIcon, editIcon, personAddIcon } from '../../components/icons';
import { usePutAdminFeed, useGetNextFeedForReview, useCheckFeedAlias, useSetFeedAlias, useDeleteFeedAlias, usePostAdminCatalog, usePutAdminCatalog, useGetFeedDuplicates, getCatalog, getCatalogCategories, generateFeedDescription, spellcheckText, subscribeFeed } from '../../connector/api';
import { getPosts } from '../../connector/post-engine';
import { FetchError, sanitizeLongText } from 't-lib-ui/connector';
import FeedPreview from '../../components/FeedPreview';
import AITextArea from '../../components/admin/AITextArea';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import FeedImageEditor from '../../components/admin/FeedImageEditor';
import { adminNotificationEngine } from '../../connector/admin-notification-engine';
import { aliasHostMismatch } from '../../utils/alias-host';
import { isHttpUrl, isYoutubeUrl } from '../../utils/url';
import { isSharedHostingDomain } from '../../utils/shared-hosting';
import { siteDomain } from '../../schema-copy/site-css';
import { getRecentCategories, recordRecentCategory } from '../../utils/recentCategories';
import { postTextBadge } from '../../schema-copy/post-stats-badges';
import { textTypeOptions, toTextType } from '../../components/admin/textTypeOptions';
import './AdminProcessFeedPage.css';

const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_MS = 180 * 24 * 60 * 60 * 1000;
const FOUR_MONTHS_MS = 120 * 24 * 60 * 60 * 1000;
const TWO_YEARS_MS = 2 * 365 * 24 * 60 * 60 * 1000;
const OVERSATURATED_CATEGORIES = ['personal-blog', 'software-development', 'comics', 'tech'];

const AdminProcessFeedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [feed, setFeed] = useState<RestAdminFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [allDone, setAllDone] = useState(false);

  // Alias
  const [isSiteUrlAlias, setIsSiteUrlAlias] = useState(false);
  const [checkingAlias, setCheckingAlias] = useState(false);
  const [deletingAlias, setDeletingAlias] = useState<string | null>(null);

  // Site URL editing
  const [siteUrlInput, setSiteUrlInput] = useState('');
  const [savingSiteUrl, setSavingSiteUrl] = useState(false);

  // Category editing (on the feed itself, independent of any catalog entry)
  const [savingCategory, setSavingCategory] = useState(false);
  // Categories of the last few approved feeds, offered as one-click shortcuts under the selectors.
  const [recentCategories, setRecentCategories] = useState<string[]>(getRecentCategories);


  // Tabs
  const [activeTab, setActiveTab] = useState<'posts' | 'site'>('posts');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  // Auto-accept countdown
  const [autoAcceptCountdown, setAutoAcceptCountdown] = useState<number | null>(null);
  const [autoAcceptReason, setAutoAcceptReason] = useState<string | null>(null);
  const autoAcceptTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Number of posts published in the last 6 months (null until computed). Feeds with <3 recent
  // posts are auto-approved; feeds with none at all are approved even faster.
  const [recentPostCount6mo, setRecentPostCount6mo] = useState<number | null>(null);

  // Follow (subscribe the current admin to the feed)
  const [following, setFollowing] = useState(false);
  const [followed, setFollowed] = useState(false);

  // Catalog mode
  const [catalogMode, setCatalogMode] = useState(false);
  const [catalog, setCatalog] = useState<RestCatalogAdmin | RestCatalog | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  const [lastModel, setLastModel] = useState('');

  // Catalog form fields
  const [description, setDescription] = useState('');
  const [catalogDescription, setCatalogDescription] = useState('');
  const [promotion, setPromotion] = useState('100');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  /** Text badge override; '' = detected from the post stats. */
  const [textType, setTextType] = useState('');
  const [paid, setPaid] = useState<boolean | null>(null);

  const magic = getAdminMagicSecret() || adminSecret || '';
  const putAdminFeed = usePutAdminFeed(id, magic);
  const getNextFeed = useGetNextFeedForReview(magic);
  const checkFeedAlias = useCheckFeedAlias(magic);
  const setFeedAlias = useSetFeedAlias(magic);
  const deleteFeedAlias = useDeleteFeedAlias(magic);
  const postAdminCatalog = usePostAdminCatalog(magic);
  const putAdminCatalog = usePutAdminCatalog(id, magic);
  const duplicatesQuery = useGetFeedDuplicates(id, magic);
  const duplicatesData = duplicatesQuery.data?.feedId == id ? duplicatesQuery.data : undefined;
  const duplicates = duplicatesData?.feeds || [];
  const sameDomain = duplicatesData?.sameDomain || [];
  const domain = siteDomain(feed?.siteUrl);
  // Other feeds on the same site (same siteUrl or same domain) need a human look — unless the
  // domain is a shared blog host like substack.com, where they're unrelated publications.
  const blocksAutoAccept = !!(duplicates.length || sameDomain.length) && !isSharedHostingDomain(domain);

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminProcessFeedPage', {
      'Escape': () => { goTo(routerCtx, '/a'); return true; },
    }, false, 0);
    return () => unregisterKeyHandler('adminProcessFeedPage');
  }, [routerCtx]);

  const cancelAutoAccept = useCallback(() => {
    if (autoAcceptTimerRef.current) {
      clearInterval(autoAcceptTimerRef.current);
      autoAcceptTimerRef.current = null;
    }
    setAutoAcceptCountdown(null);
    setAutoAcceptReason(null);
  }, []);

  // Count how many of the newest posts fall within the last 6 months. Only the newest posts
  // matter: if the 3rd-newest is already older than 6 months there are <3 recent posts. Reuses the
  // same getPosts cache FeedPreview populates, so this is essentially free.
  useEffect(() => {
    setRecentPostCount6mo(null);
    if (!feed) return;
    const pc = feed.postCount;
    if (!pc) { setRecentPostCount6mo(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const startIndex = Math.max(0, pc - 20);
        const posts = await getPosts({ ...feed, postCount: pc } as any, startIndex, pc);
        const cutoff = Date.now() - SIX_MONTHS_MS;
        const count = posts.filter(p => p.pubDate && new Date(p.pubDate).getTime() >= cutoff).length;
        if (!cancelled) setRecentPostCount6mo(count);
      } catch (e) {
        console.error('Failed to count recent posts:', e);
        if (!cancelled) setRecentPostCount6mo(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed?.id]);

  // Auto-accept old active feeds or auto-disable bad feeds
  useEffect(() => {
    // Wait for the duplicate/same-domain lookup of this very feed, which can veto auto-accepting.
    if (!feed || loading || allDone || catalogMode || processing || checkingAlias || duplicatesData?.feedId != feed.id) return;

    const postAge = feed.lastNewPostPubDate
      ? Date.now() - new Date(feed.lastNewPostPubDate).getTime()
      : null;
    const isOversaturated = OVERSATURATED_CATEGORIES.some(c => feed.category?.startsWith(c));
    // Never auto-accept a feed with a missing/invalid site URL — it must be fixed manually first.
    const canAutoAccept = feed.feedStatus == 'ACTIVE' && isHttpUrl(feed.siteUrl) && !blocksAutoAccept;
    const isOldActive = canAutoAccept && postAge != null
      && (postAge > TWO_YEARS_MS || (isOversaturated && postAge > FOUR_MONTHS_MS));
    // YouTube channel feeds are trustworthy and uniform — auto-approve (never cataloged).
    const isYoutubeChannel = canAutoAccept && isYoutubeUrl(feed.url);
    // Personal blogs are low-risk and high-volume in the queue — auto-approve them quickly.
    const isPersonalBlog = canAutoAccept && !!feed.category?.startsWith('personal-blog');
    // Feeds that publish rarely (fewer than 3 posts in the last 6 months, including feeds with no
    // posts at all) are low-risk — auto-approve them. Feeds with no posts go even faster.
    const noPosts = canAutoAccept && !feed.postCount;
    const fewRecentPosts = canAutoAccept && recentPostCount6mo != null && recentPostCount6mo < 3;
    // Non-English feeds are lower-priority for our (English) catalog — auto-approve them quickly.
    const isNonEnglish = canAutoAccept && !!feed.language && !feed.language.toLowerCase().startsWith('en');
    const isBad = feed.feedStatus == 'BAD';

    const willAccept = isOldActive || isYoutubeChannel || isPersonalBlog || noPosts || fewRecentPosts || isNonEnglish;
    if (!willAccept && !isBad) return;

    const autoAction = () => {
      if (isBad)
        handleStatusAndNext('BAD');
      else
        handleAcceptAndNext();
    };

    const countdown = isBad ? 10
      : noPosts ? 2
      : isOldActive ? 2
      : (isPersonalBlog || fewRecentPosts || isYoutubeChannel || isNonEnglish) ? 5
      : 2;
    // Brief reason for the countdown UI — kept short since there are only a few seconds to read it.
    const reason = isBad ? 'failing to fetch'
      : noPosts ? 'no posts'
      : isOldActive ? (postAge != null && postAge > TWO_YEARS_MS ? 'inactive 2+ years' : 'oversaturated category, inactive')
      : isYoutubeChannel ? 'YouTube channel'
      : isPersonalBlog ? 'personal blog'
      : fewRecentPosts ? '<3 posts in 6 months'
      : isNonEnglish ? `non-English (${feed.language})`
      : '';
    setAutoAcceptCountdown(countdown);
    setAutoAcceptReason(reason);
    autoAcceptTimerRef.current = setInterval(() => {
      setAutoAcceptCountdown(prev => {
        if (prev == null) return null;
        if (prev <= 1) {
          clearInterval(autoAcceptTimerRef.current!);
          autoAcceptTimerRef.current = null;
          autoAction();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => cancelAutoAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed?.id, loading, allDone, catalogMode, checkingAlias, recentPostCount6mo, duplicatesData]);

  // Load feed
  useEffect(() => {
    const fetchFeed = async () => {
      try {
        setLoading(true);
        setError(false);
        setCatalogMode(false);
        setHasAutoGenerated(false);
        setIframeUrl(null);
        setActiveTab('posts');

        let result: RestAdminFeed;
        if (id == 'next') {
          result = await getNextFeed();
          goTo(routerCtx, `/a/admin/process/${result.id}`, 'replace');
          return;
        } else {
          result = await authenticatedRequest<RestAdminFeed>(
            nonNull(appState.state.authData),
            'GET', 'api',
            `/api/admin/feed/${id}`,
            false, { magic }
          );
        }

        setFeed(result);
        setSiteUrlInput(result.siteUrl || '');
        setAllDone(false);
        setFollowed(false);
        // Reset in the same render feed changes so the auto-accept effect never evaluates the new
        // feed against the previous feed's recent-post count.
        setRecentPostCount6mo(null);
      } catch (e) {
        if (id == 'next' && e instanceof FetchError && e.status == 404) {
          adminNotificationEngine.resetCountToZero();
          setAllDone(true);
          setLoading(false);
        } else {
          console.error('Failed to load feed:', e);
          setError(true);
          setLoading(false);
        }
        return;
      }
      setLoading(false);
    };

    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appState.state.authData, magic, routerCtx]);

  // Check alias
  useEffect(() => {
    const check = async () => {
      if (!feed || !feed.siteUrl || feed.siteUrl == feed.url) {
        setIsSiteUrlAlias(false);
        return;
      }
      try {
        setCheckingAlias(true);
        const result = await checkFeedAlias({ feedId: feed.id, url: feed.siteUrl });
        setIsSiteUrlAlias(result.isAlias);
      } catch (e) {
        console.error('Failed to check alias:', e);
        setIsSiteUrlAlias(false);
      } finally {
        setCheckingAlias(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed?.id, feed?.siteUrl]);

  // Auto-generate catalog description when entering catalog mode
  useEffect(() => {
    if (!catalogMode || !feed || catalog || catalogDescription.trim() || hasAutoGenerated || catalogLoading)
      return;
    const autoGen = async () => {
      try {
        setHasAutoGenerated(true);
        // The AI feed analysis already produced a one-sentence catalog description on the initial
        // fetch — use it and skip the extra generation call.
        if (feed.aiDescription?.trim()) {
          setCatalogDescription(feed.aiDescription.trim());
          return;
        }
        const result = await generateFeedDescription(nonNull(appState.state.authData), id, undefined);
        setCatalogDescription(result.description);
        setLastModel(result.model);
      } catch (e) {
        console.error('Failed to auto-generate description:', e);
      }
    };
    autoGen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogMode, feed, catalog, catalogDescription, hasAutoGenerated, catalogLoading]);

  const trySetAlias = async () => {
    if (!feed || !feed.siteUrl || feed.siteUrl == feed.url || isSiteUrlAlias) return;
    try {
      // No force: if the siteUrl already maps to a different feed, skip rather than block
      // the review flow. The admin can resolve the conflict on the feed detail page.
      const res = await setFeedAlias({ feedId: feed.id, url: feed.siteUrl });
      if (res.conflict) {
        console.warn('siteUrl alias conflict, not overwriting:', res.conflict);
        return;
      }
      setIsSiteUrlAlias(true);
    } catch (e) {
      console.error('Failed to set alias:', e);
    }
  };

  // Removes an alias. Pending (unpublished) aliases are removed without confirmation;
  // published aliases are authoritative for URL lookups, so we confirm before deleting.
  const handleDeleteAlias = async (alias: string, pending: boolean) => {
    if (!feed || deletingAlias) return;
    if (!pending && !window.confirm(`Remove published alias "${alias}"?\n\nURL lookups using it will stop resolving to this feed.`)) return;

    try {
      setDeletingAlias(alias);
      await deleteFeedAlias({ feedId: feed.id, alias, pending });

      // Reload feed so the alias lists reflect the change
      const result = await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'GET', 'api',
        `/api/admin/feed/${feed.id}`,
        false, { magic }
      );
      setFeed(result);
    } catch (e) {
      console.error('Failed to delete alias:', e);
    } finally {
      setDeletingAlias(null);
    }
  };

  const handleSaveSiteUrl = async () => {
    if (!feed || savingSiteUrl) return;
    const trimmed = siteUrlInput.trim();
    if (!isHttpUrl(trimmed)) return;
    try {
      setSavingSiteUrl(true);
      const updatedFeed = await putAdminFeed({ siteUrl: trimmed });
      setFeed(updatedFeed);
      setSiteUrlInput(updatedFeed.siteUrl || '');
    } catch (e) {
      console.error('Failed to update site URL:', e);
    } finally {
      setSavingSiteUrl(false);
    }
  };

  // Updates the feed's category directly, without requiring a catalog entry.
  const handleCategoryChange = async (newCategory: string) => {
    if (!feed || savingCategory || newCategory == (feed.category || '')) return;
    try {
      setSavingCategory(true);
      const updatedFeed = await putAdminFeed({ category: newCategory });
      setFeed(updatedFeed);
    } catch (e) {
      console.error('Failed to update category:', e);
    } finally {
      setSavingCategory(false);
    }
  };

  // Subscribe the current admin to this feed (does not advance to the next feed).
  const handleFollow = async () => {
    if (!feed || following || followed) return;
    try {
      setFollowing(true);
      await subscribeFeed(nonNull(appState.state.authData), feed.id);
      setFollowed(true);
    } catch (e) {
      console.error('Failed to follow feed:', e);
    } finally {
      setFollowing(false);
    }
  };

  const handleStatusAndNext = async (feedStatus: 'BLOCK' | 'ACTIVE' | 'BAD') => {
    if (!feed || processing) return;
    try {
      setProcessing(true);
      await putAdminFeed({ feedStatus, manuallyVerified: true });
      adminNotificationEngine.decrementCount();
      try {
        const nextFeed = await getNextFeed();
        goTo(routerCtx, `/a/admin/process/${nextFeed.id}`, 'silent');
      } catch (e) {
        if (e instanceof FetchError && e.status == 404) {
          adminNotificationEngine.resetCountToZero();
          setAllDone(true);
        }
        else throw e;
      }
    } catch (e) {
      console.error('Failed to process feed:', e);
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptAndNext = async () => {
    if (!feed || processing || !isHttpUrl(feed.siteUrl)) return;
    try {
      setProcessing(true);
      await trySetAlias();
      await putAdminFeed({ manuallyVerified: true });
      setRecentCategories(recordRecentCategory(feed.category));
      adminNotificationEngine.decrementCount();
      try {
        const nextFeed = await getNextFeed();
        goTo(routerCtx, `/a/admin/process/${nextFeed.id}`, 'silent');
      } catch (e) {
        if (e instanceof FetchError && e.status == 404) {
          adminNotificationEngine.resetCountToZero();
          setAllDone(true);
        }
        else throw e;
      }
    } catch (e) {
      console.error('Failed to accept feed:', e);
    } finally {
      setProcessing(false);
    }
  };

  const handleEnterCatalogMode = async () => {
    if (!feed) return;
    setCatalogLoading(true);
    setCatalogMode(true);

    try {
      const [cats] = await Promise.all([
        getCatalogCategories(nonNull(appState.state.authData)),
      ]);
      setCategories(cats.categories);

      let catalogResult: RestCatalogAdmin | RestCatalog | null = null;
      try {
        catalogResult = await getCatalog(nonNull(appState.state.authData), id);
        setCatalog(catalogResult);
      } catch (e) {
        if (e instanceof FetchError && e.status == 404)
          setCatalog(null);
        else throw e;
      }

      if (catalogResult) {
        setDescription(catalogResult.description || '');
        setCatalogDescription(catalogResult.catalogDescription || '');
        setPromotion(String(catalogResult.promotion || 100));
        setCategory(catalogResult.category || '');
        setType(catalogResult.type || '');
        setTextType(catalogResult.textType ?? '');
        setPaid(catalogResult.paid ?? null);
      } else {
        setDescription(feed.description || '');
        setCatalogDescription('');
        setPromotion('100');
        setCategory(feed.category || '');
        setType(feed.type || '');
        setTextType('');
        setPaid(feed.paid ?? null);
      }
    } catch (e) {
      console.error('Failed to load catalog data:', e);
      setCatalogMode(false);
    } finally {
      setCatalogLoading(false);
    }
  };

  const handlePublishCatalog = async () => {
    if (!feed || saving || !isHttpUrl(feed.siteUrl)) return;
    try {
      setSaving(true);
      await trySetAlias();

      const promotionNum = parseInt(promotion, 10);
      if (isNaN(promotionNum) || promotionNum < 0) return;

      // Auto-derive fields not shown in UI
      const title = feed.shortTitle || feed.title || '';
      const keywordArray = (feed.keywords || []);
      let domain = '';
      try {
        const url = new URL(feed.siteUrl);
        domain = url.hostname.replace(/^www\./, '');
      } catch (_e) { /* ignore */ }

      if (catalog) {
        const update: RestCatalogUpdate = {
          title,
          description,
          catalogDescription,
          promotion: promotionNum,
          keywords: keywordArray.length ? keywordArray : undefined,
          category: category || undefined,
          type: type || undefined,
          // null clears a previous override
          textType: toTextType(textType) ?? null,
          domain,
          paid,
        };
        await putAdminCatalog(update);
      } else {
        const create: RestCatalogCreate = {
          feedId: id,
          title,
          description,
          catalogDescription,
          active: true,
          promotion: promotionNum,
          keywords: keywordArray.length ? keywordArray : undefined,
          category: category || undefined,
          type: type || undefined,
          textType: toTextType(textType),
          domain,
          paid,
        };
        await postAdminCatalog(create);
      }

      // Verify + next
      await putAdminFeed({ manuallyVerified: true });
      setRecentCategories(recordRecentCategory(category));
      adminNotificationEngine.decrementCount();
      try {
        const nextFeed = await getNextFeed();
        goTo(routerCtx, `/a/admin/process/${nextFeed.id}`, 'silent');
      } catch (e) {
        if (e instanceof FetchError && e.status == 404) {
          adminNotificationEngine.resetCountToZero();
          setAllDone(true);
        }
        else throw e;
      }
    } catch (e) {
      console.error('Failed to publish catalog:', e);
    } finally {
      setSaving(false);
    }
  };

  // Last post date color
  const getLastPostDateStyle = (): React.CSSProperties => {
    if (!feed?.lastNewPostPubDate) return {};
    const age = Date.now() - new Date(feed.lastNewPostPubDate).getTime();
    if (age > SIX_MONTHS_MS) return { color: 'var(--error-color)', fontWeight: 700 };
    if (age > THREE_MONTHS_MS) return { color: '#cc7a00', fontWeight: 600 };
    return {};
  };

  const openInSiteTab = (url: string) => {
    setIframeUrl(url);
    setActiveTab('site');
  };

  const isAdult = feed && (feed.category == 'adult' || feed.downreadPrivateComment?.toLowerCase().includes('adult'));
  const isContentWarning = feed && (isAdult || feed.category == 'piracy+illegal');
  const isBadOrBlocked = feed && (feed.feedStatus == 'BAD' || feed.feedStatus == 'BLOCK');
  const isActiveOrBlocked = feed && (feed.feedStatus == 'ACTIVE' || feed.feedStatus == 'BLOCK');
  // YouTube channel feeds must not be cataloged — hide the Catalog action for them.
  const isYoutubeFeed = feed && isYoutubeUrl(feed.url);
  // A valid site URL is required before a feed can be accepted or cataloged.
  const siteUrlValid = isHttpUrl(feed?.siteUrl);

  // Options for editing the feed's own category (independent of the catalog category list,
  // which is only loaded in catalog mode). Includes the current value even if it is no longer
  // in FeedCategoryList, so the dropdown never shows an empty selection for a set category.
  const feedCategoryOptions: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...FeedCategoryList.map(cat => ({ id: cat, text: cat, searchTextLc: cat.toLowerCase() })),
    ...(feed?.category && !FeedCategoryList.includes(feed.category as any)
      ? [{ id: feed.category, text: feed.category, searchTextLc: feed.category.toLowerCase() }]
      : [])
  ];

  // Category options
  const categoryOptions: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...categories.map(cat => ({
      id: cat,
      text: cat.charAt(0).toUpperCase() + cat.slice(1),
      searchTextLc: cat.toLowerCase()
    }))
  ];

  const typeOptions: DropDownSelectorValue[] = [
    { id: '', text: '(None)', searchTextLc: 'none' },
    ...RssTypes.map(t => ({
      id: t,
      text: t.charAt(0).toUpperCase() + t.slice(1),
      searchTextLc: t.toLowerCase()
    }))
  ];

  const paidOptions: DropDownSelectorValue[] = [
    { id: 'unknown', text: 'Unknown', searchTextLc: 'unknown' },
    { id: 'free', text: 'Free', searchTextLc: 'free' },
    { id: 'paid', text: 'Paid', searchTextLc: 'paid' }
  ];

  const paidDropdownValue = paid === true ? 'paid' : paid === false ? 'free' : 'unknown';

  // Shared so the duplicate-site warning stays visible during the catalog/approval step too,
  // not only on the feed-info view.
  const renderDuplicatesWarning = () => duplicates.length > 0 && (
    <div style={{ padding: '12px', background: '#fff3cd', borderRadius: 6, marginTop: 12, marginBottom: 12, border: '1px solid #ffc107' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: '1.1em', color: '#856404' }}>{'⚠'}</span>
        <span style={{ fontWeight: 600, color: '#856404' }}>
          {duplicates.length} duplicate feed{duplicates.length == 1 ? '' : 's'} share{duplicates.length == 1 ? 's' : ''} this site URL
        </span>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem' }}>
        {duplicates.map(d => (
          <li key={d.id} style={{ marginBottom: 3 }}>
            <A href={`/a/admin/feed/${d.id}`} internal={true}>{d.shortTitle || d.title || d.id}</A>
            {' '}
            <span style={{ color: 'var(--gray-500)' }}>
              [{d.feedStatus}{d.manuallyVerified ? ', verified' : ''}, {d.subscriberCount} sub{d.subscriberCount == 1 ? '' : 's'}]
            </span>
            {' — '}
            <span style={{ color: 'var(--gray-350)', wordBreak: 'break-all' }}>{sanitizeLongText(d.url, 70)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderSameDomain = () => sameDomain.length > 0 && (
    <div style={{ padding: '12px', background: 'var(--gray-975)', borderRadius: 6, marginTop: 12, marginBottom: 12, border: '1px solid var(--gray-850)' }}>
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontWeight: 600, color: 'var(--gray-200)' }}>
          {sameDomain.length == 10 ? '10+' : sameDomain.length} other feed{sameDomain.length == 1 ? '' : 's'} on {domain}
        </span>
        {blocksAutoAccept && <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}> — no auto-accept</span>}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem' }}>
        {sameDomain.map(d => (
          <li key={d.id} style={{ marginBottom: 3 }}>
            <A href={`/a/admin/feed/${d.id}`} internal={true}>{d.shortTitle || d.title || d.id}</A>
            {' '}
            <span style={{ color: 'var(--gray-500)' }}>
              [{d.feedStatus}{d.manuallyVerified ? ', verified' : ''}, {d.subscriberCount} sub{d.subscriberCount == 1 ? '' : 's'}]
            </span>
            {' — '}
            <span style={{ color: 'var(--gray-350)', wordBreak: 'break-all' }}>{sanitizeLongText(d.siteUrl, 70)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  // Quick-access shortcuts for the categories of the last few approved feeds. Only categories the
  // given selector actually offers are shown, and never the currently selected one.
  const renderRecentCategories = (available: readonly string[], current: string, onPick: (cat: string) => void) => {
    const shown = recentCategories.filter(c => c != current && available.includes(c));
    if (!shown.length) return null;
    return <div className="processRecentCats">
      <span className="processRecentCatsLabel">Recent:</span>
      {shown.map(c => (
        <button key={c} type="button" className="processRecentCat" onClick={() => onPick(c)}>{c}</button>
      ))}
    </div>;
  };

  // Lets an admin subscribe to the feed they are reviewing. Shown at the bottom of both the main
  // review section and the catalog editor.
  const renderFollowButton = () => feed && (
    <div style={{ marginTop: 12 }}>
      <TextButton
        label={followed ? 'Following' : 'Follow'}
        icon={followed ? checkmarkIcon : personAddIcon}
        onClick={handleFollow}
        focussable="flow"
        type="minor"
        size="small"
        busy={following}
        enabled={followed ? 'disabled' : 'enabled'}
      />
    </div>
  );

  const renderFeedInfo = () => {
    if (!feed) return null;

    return <>
      <h2>{feed.title || 'Untitled'}</h2>
      {feed.shortTitle && <div className="shortTitle">&rarr; {feed.shortTitle}</div>}

      {isContentWarning && (
        <div className="processContentWarning">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2em' }}>{'⚠'}</span>
            <span style={{ fontWeight: 600, color: '#721c24' }}>{isAdult ? 'Adult Content' : 'Illegal Content'}</span>
          </div>
          <div style={{ marginTop: 12 }}>
            <TextButton
              label="Block"
              icon={closeIcon}
              onClick={() => handleStatusAndNext('BLOCK')}
              focussable="flow"
              type="major"
              busy={processing}
            />
          </div>
        </div>
      )}

      {renderDuplicatesWarning()}
      {renderSameDomain()}

      <div className="processInfoRow">
        <span className="processInfoLabel">Status</span>
        <span className={`processStatusBadge ${feed.feedStatus}`}>{feed.feedStatus}</span>
      </div>

      <div className="processInfoRow">
        <span className="processInfoLabel">Created</span>
        <span className="processInfoValue">{formatCompactDate(feed.createdAt)}</span>
      </div>

      {feed.lastNewPostPubDate && (
        <div className="processInfoRow">
          <span className="processInfoLabel">Last Post</span>
          <span className="processInfoValue" style={getLastPostDateStyle()}>
            {formatCompactDate(feed.lastNewPostPubDate)}
          </span>
        </div>
      )}

      <div className="processInfoRow">
        <span className="processInfoLabel">RSS</span>
        <span className="processInfoValue">
          <a href={feed.url} onClick={e => { e.preventDefault(); openInSiteTab(feed.url); }}>{sanitizeLongText(feed.url, 60)}</a>
        </span>
      </div>

      <div className="processInfoRow" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <span className="processInfoLabel">Site</span>
        {feed.siteUrl && (
          <span className="processInfoValue" style={{ marginTop: 2 }}>
            <a href={feed.siteUrl} onClick={e => { e.preventDefault(); openInSiteTab(feed.siteUrl); }}>{sanitizeLongText(feed.siteUrl, 60)}</a>
          </span>
        )}
        {(() => {
          const trimmed = siteUrlInput.trim();
          const validInput = isHttpUrl(trimmed);
          const hasChanged = trimmed != (feed.siteUrl || '');
          return <div style={{ marginTop: 4 }}>
            <Input value={siteUrlInput} onInput={setSiteUrlInput} placeholder="https://example.com" layout="singleCol" />
            {trimmed && !validInput && (
              <div style={{ marginTop: 4, fontSize: '0.8rem', color: 'var(--error-color)' }}>Must start with http:// or https://</div>
            )}
            {hasChanged && (
              <div style={{ marginTop: 6 }}>
                <TextButton
                  label="Save Site URL"
                  icon={checkmarkIcon}
                  onClick={handleSaveSiteUrl}
                  focussable="flow"
                  type="major"
                  size="small"
                  busy={savingSiteUrl}
                  enabled={validInput ? 'enabled' : 'disabled'}
                />
              </div>
            )}
          </div>;
        })()}
      </div>

      {domain && (
        <div className="processInfoRow">
          <span className="processInfoLabel">Domain</span>
          <span className="processInfoValue">{domain}</span>
        </div>
      )}

      {/* Aliases + pending aliases: surface BEFORE the reviewer clicks Accept (which promotes
          pending aliases into authoritative feedAliases). Warn on host mismatches vs siteUrl. */}
      {feed.aliases?.length ? (
        <div className="processInfoRow" style={{ flexDirection: 'column' }}>
          <span className="processInfoLabel">Aliases</span>
          <span className="processInfoValue" style={{ fontSize: '0.85rem', marginTop: 2 }}>
            {feed.aliases.map(a => (
              <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {a}{aliasHostMismatch(a, feed.siteUrl) && <span title="Alias host differs from site URL host" style={{ color: 'var(--error-color)', fontWeight: 600 }}>⚠</span>}
                <TextButton label="Remove" icon={closeCircleIcon} onClick={() => handleDeleteAlias(a, false)} focussable="flow" type="minor" size="small" busy={deletingAlias == a} />
              </div>
            ))}
          </span>
        </div>
      ) : null}
      {feed.pendingAliases?.length ? (
        <div className="processInfoRow" style={{ flexDirection: 'column' }}>
          <span className="processInfoLabel" style={{ color: '#856404' }}>Pending aliases (promoted on Accept)</span>
          <span className="processInfoValue" style={{ fontSize: '0.85rem', marginTop: 2 }}>
            {feed.pendingAliases.map(a => (
              <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {a}{aliasHostMismatch(a, feed.siteUrl) && <span title="Pending alias host differs from site URL host — verify this is legitimate before accepting" style={{ color: 'var(--error-color)', fontWeight: 600 }}>⚠ host mismatch</span>}
                <TextButton label="Remove" icon={closeCircleIcon} onClick={() => handleDeleteAlias(a, true)} focussable="flow" type="minor" size="small" busy={deletingAlias == a} />
              </div>
            ))}
          </span>
        </div>
      ) : null}

      {feed.downreadPublicComment && (
        <div className="processInfoRow" style={{ flexDirection: 'column' }}>
          <span className="processInfoLabel">Public Comment</span>
          <span className="processInfoValue" style={{ fontSize: '0.85rem', color: 'var(--gray-350)', marginTop: 2 }}>
            {feed.downreadPublicComment}
          </span>
        </div>
      )}

      {feed.downreadPrivateComment && (
        <div className="processInfoRow" style={{ flexDirection: 'column' }}>
          <span className="processInfoLabel">Private Comment</span>
          <span className="processInfoValue" style={{ fontSize: '0.85rem', color: '#856404', marginTop: 2 }}>
            {feed.downreadPrivateComment}
          </span>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <TextButton
          label="View Full Details"
          icon={editIcon}
          onClick={() => goTo(routerCtx, `/a/admin/feed/${id}`)}
          focussable="flow"
          size="small"
        />
      </div>

      <hr className="processDivider" />

      {/* Status-dependent info */}
      {feed.feedStatus == 'BAD' && feed.fetchErrorCount > 0 && (
        <div className="processInfoRow">
          <span className="processInfoLabel">Errors</span>
          <span className="processInfoValue" style={{ color: 'var(--error-color)', fontWeight: 600 }}>
            {feed.fetchErrorCount} fetch error{feed.fetchErrorCount != 1 ? 's' : ''}
          </span>
        </div>
      )}

      {isActiveOrBlocked && <>
        <div className="processInfoRow">
          <span className="processInfoLabel">Category</span>
          <span className="processInfoValue" style={{ flex: 1 }}>
            <DropDownSelector
              selections={feedCategoryOptions}
              value={feed.category || ''}
              onSelect={v => handleCategoryChange(v.id)}
              focussable="flow"
              disabled={savingCategory}
            />
            {!savingCategory && renderRecentCategories(FeedCategoryList, feed.category || '', handleCategoryChange)}
          </span>
        </div>
        {feed.type && (
          <div className="processInfoRow">
            <span className="processInfoLabel">Type</span>
            <span className="processInfoValue">{feed.type}</span>
          </div>
        )}
        {feed.description && (
          <div className="processInfoRow" style={{ flexDirection: 'column' }}>
            <span className="processInfoLabel">Description</span>
            <span className="processInfoValue" style={{ fontSize: '0.85rem', color: 'var(--gray-350)', marginTop: 2 }}>
              {feed.description.length > 200 ? feed.description.slice(0, 200) + '...' : feed.description}
            </span>
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <div className="processInfoLabel" style={{ marginBottom: 6 }}>Image</div>
          <FeedImageEditor feed={feed} magic={magic} onUpdated={setFeed} />
        </div>
      </>}

      {/* Auto-accept countdown */}
      {autoAcceptCountdown != null && (
        <div className="processInfoRow" style={{ marginTop: 12, padding: '8px 12px', background: '#fff3cd', borderRadius: 6 }}>
          <span style={{ fontWeight: 600 }}>
            {feed.feedStatus == 'BAD' ? 'Auto-disabling' : 'Auto-accepting'} in {autoAcceptCountdown}s…
            {autoAcceptReason && <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> ({autoAcceptReason})</span>}
          </span>
          <TextButton
            label="Cancel"
            icon={closeIcon}
            onClick={cancelAutoAccept}
            focussable="flow"
            size="small"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="processActions">
        {!siteUrlValid && !isBadOrBlocked && (
          <div style={{ padding: '8px 12px', marginBottom: 8, background: '#f8d7da', borderRadius: 6, border: '1px solid #f5c6cb', color: '#721c24', fontSize: '0.85rem' }}>
            <span style={{ fontWeight: 600 }}>Invalid site URL.</span> Fix the site URL above (must start with http:// or https://) before accepting or cataloging.
          </div>
        )}
        <ButtonBar size="small">
          {isBadOrBlocked ? <>
            <TextButton
              label="Disable"
              icon={closeCircleIcon}
              onClick={() => handleStatusAndNext('BAD')}
              focussable="flow"
              busy={processing}
            />
            <TextButton
              label="Block"
              icon={thumbsDownIcon}
              onClick={() => handleStatusAndNext('BLOCK')}
              focussable="flow"
              busy={processing}
            />
          </> : <>
            <TextButton
              label="Accept"
              icon={thumbsUpIcon}
              onClick={handleAcceptAndNext}
              focussable="flow"
              type="major"
              busy={processing}
              enabled={siteUrlValid ? 'enabled' : 'disabled'}
            />
            <TextButton
              label="Block"
              icon={thumbsDownIcon}
              onClick={() => handleStatusAndNext('BLOCK')}
              focussable="sideway"
              busy={processing}
            />
            {feed.feedStatus != 'PRIVATE' && !isYoutubeFeed && (
              <TextButton
                label="Catalog"
                icon={editIcon}
                onClick={handleEnterCatalogMode}
                focussable="flow"
                enabled={siteUrlValid ? 'enabled' : 'disabled'}
              />
            )}
          </>}
        </ButtonBar>
      </div>

      {renderFollowButton()}
    </>;
  };

  const renderCatalogEdit = () => {
    if (!feed) return null;

    if (catalogLoading)
      return <div className="processLoading"><Spinner spinning={true} /> Loading catalog data...</div>;

    return <>
      <div className="shortTitle">{feed.title || feed.shortTitle}</div>

      {renderDuplicatesWarning()}
      {renderSameDomain()}

      <div className="processCatalogField">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label>Description</label>
          {description && (
            <TextButton label="Clear" onClick={() => setDescription('')} focussable="sideway" type="minor" size="small" />
          )}
        </div>
        <TextArea
          value={description}
          onInput={setDescription}
          rows={2}
          layout="singleCol"
          borderless={false}
        />
      </div>

      <div className="processCatalogField">
        <AITextArea
          value={catalogDescription}
          onInput={setCatalogDescription}
          placeholder="Description for catalog display"
          rows={5}
          label="Catalog Description"
          required={true}
          onGenerate={async (instructions) => {
            const result = await generateFeedDescription(nonNull(appState.state.authData), id, instructions);
            setLastModel(result.model);
            return result.description;
          }}
          onImprove={async (text, instructions) => {
            return await spellcheckText(nonNull(appState.state.authData), text, instructions);
          }}
          extraButtons={catalogDescription.includes('.') && (
            <TextButton label="Shorten" type="minor" size="small" onClick={() => {
              const dot = catalogDescription.indexOf('.');
              if (dot >= 0) setCatalogDescription(catalogDescription.slice(0, dot + 1));
            }} />
          )}
        />
        {lastModel && <div style={{ fontSize: '0.7rem', color: 'var(--gray-600)', marginTop: '-8px' }}>{lastModel}</div>}
      </div>

      <div className="processCatalogField">
        <label>Promotion Priority</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input value={promotion} onInput={setPromotion} placeholder="100" layout="singleCol" />
          <span style={{ display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
            {[1, 10, 100].map(n => (
              <button key={n} type="button" style={{ fontSize: '0.8rem', background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', padding: 0 }} onClick={() => setPromotion(String((parseInt(promotion, 10) || 0) + n))}>+{n}</button>
            ))}
            <button type="button" style={{ fontSize: '0.8rem', background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', padding: 0 }} onClick={() => setPromotion('100')}>100</button>
          </span>
        </div>
      </div>

      <div className="processCatalogField">
        <label>Feed Image</label>
        <FeedImageEditor feed={feed} magic={magic} allowCustomCreation={true} onUpdated={setFeed} />
      </div>

      <div className="processCatalogField">
        <label>Category</label>
        <DropDownSelector selections={categoryOptions} value={category} onSelect={v => setCategory(v.id)} focussable="flow" />
        {renderRecentCategories(categories, category, setCategory)}
      </div>

      <div className="processCatalogField">
        <label>Type</label>
        <DropDownSelector selections={typeOptions} value={type} onSelect={v => setType(v.id)} focussable="flow" />
      </div>

      <div className="processCatalogField">
        <label>Content Type</label>
        <DropDownSelector
          selections={textTypeOptions(postTextBadge(feed.postStats, category || undefined))}
          value={textType}
          onSelect={v => setTextType(v.id)}
          focussable="flow"
        />
      </div>

      <div className="processCatalogField">
        <label>Paid/Free</label>
        <DropDownSelector
          selections={paidOptions}
          value={paidDropdownValue}
          onSelect={v => setPaid(v.id === 'paid' ? true : v.id === 'free' ? false : null)}
          focussable="flow"
        />
      </div>

      <div className="processActions">
        <ButtonBar size="small">
          <TextButton
            label="Publish"
            icon={checkmarkIcon}
            onClick={handlePublishCatalog}
            focussable="flow"
            type="major"
            busy={saving}
            enabled={catalogDescription.trim() && siteUrlValid ? 'enabled' : 'disabled'}
          />
          <TextButton
            label="Abort"
            icon={closeIcon}
            onClick={() => setCatalogMode(false)}
            focussable="flow"
          />
        </ButtonBar>
      </div>

      {renderFollowButton()}
    </>;
  };

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowForm main={true}>
          <div className="processPage">
            <div className="processPageHeader">
              <TextButton
                label="Back"
                icon={chevronBackIcon}
                onClick={() => goTo(routerCtx, '/a')}
                focussable="flow"
                size="small"
              />
              {feed && <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                Processing: {feed.shortTitle || feed.title}
              </span>}
            </div>

            {allDone && (
              <div className="processDone">
                <h2>All Done!</h2>
                <p style={{ color: 'var(--gray-400)' }}>No more feeds require review.</p>
                <A href="/a" internal={true}>Back to Reader</A>
              </div>
            )}

            {loading && <div className="processLoading"><Spinner spinning={true} /> Loading...</div>}
            {error && <div className="processError">Error loading feed!</div>}

            {feed && !loading && !allDone && (
              <div className="processPageContent">
                <div className="processPageLeft">
                  {catalogMode ? renderCatalogEdit() : renderFeedInfo()}
                </div>
                <div className="processPageRight">
                  <div className="processPageTabs">
                    <button
                      className={`processPageTab ${activeTab == 'posts' ? 'active' : ''}`}
                      onClick={() => setActiveTab('posts')}
                    >Posts</button>
                    <button
                      className={`processPageTab ${activeTab == 'site' ? 'active' : ''}`}
                      onClick={() => setActiveTab('site')}
                    >Site</button>
                  </div>
                  <div className="processPageTabContent">
                    {activeTab == 'posts' && <FeedPreview feed={feed} maxPosts={20} />}
                    {activeTab == 'site' && (iframeUrl || feed.siteUrl) && (
                      <iframe
                        key={iframeUrl || feed.siteUrl}
                        src={iframeUrl || feed.siteUrl}
                        sandbox="allow-scripts allow-same-origin"
                        title="Feed website"
                      />
                    )}
                    {activeTab == 'site' && !iframeUrl && !feed.siteUrl && (
                      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
                        No site URL available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </FlowForm>
      )}
    </>
  );
};

export default AdminProcessFeedPage;
